import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTenant } from '../contexts/TenantContext';
import { tenantStorage } from '../utils/storage';
import { isValidAffiliateId, writeAttribution } from '../utils/attribution';
import { apiHostBase } from '../services/api';

// Bare host (no /api). The tracking endpoint is at `/t`, served at the
// backend root, not under `/api`. `apiHostBase()` throws if VITE_API_URL
// is missing — no silent prod fallback.
const API_BASE_URL = apiHostBase();

/**
 * Component to track affiliate referral links
 * Automatically tracks clicks when ?ref= parameter is present in URL.
 *
 * Also owns last-touch attribution writes. Previously `writeAttribution`
 * fired in the axios request interceptor on every API call — a page-load
 * with `?ref=X` re-stamped attribution N times per session. It now fires
 * once per navigation here, keyed on `location.search`, which is the only
 * place a `?ref` change can show up.
 */
export const AffiliateTracker = () => {
  const location = useLocation();
  const { tenant } = useTenant();

  useEffect(() => {
    if (!tenant) return;

    // Parse URL parameters
    const searchParams = new URLSearchParams(location.search);
    const refCode = searchParams.get('ref');
    let productIdentifier = searchParams.get('product');

    // Extract product ID from URL path if on product page
    // URL format: /products/27 or /products/some-slug
    const pathMatch = location.pathname.match(/^\/products\/([^\/]+)/);
    if (pathMatch && pathMatch[1]) {
      productIdentifier = pathMatch[1]; // Can be ID or slug
    }

    // Last-touch attribution stamp — once per navigation, not per request.
    // Accept either ?ref= or the older ?affiliate= alias.
    const affiliateIdFromUrl =
      searchParams.get('ref') || searchParams.get('affiliate');
    if (affiliateIdFromUrl && isValidAffiliateId(affiliateIdFromUrl)) {
      writeAttribution(affiliateIdFromUrl);
    }

    if (refCode) {
      // Track the click
      trackAffiliateClick(refCode, productIdentifier || undefined, searchParams);
    }
  }, [location.search, location.pathname, tenant]);

  const trackAffiliateClick = async (
    refCode: string,
    productSlug: string | undefined,
    searchParams: URLSearchParams
  ) => {
    try {
      // Build tracking URL
      const trackingUrl = new URL(`${API_BASE_URL}/t`);
      trackingUrl.searchParams.set('ref', refCode);

      // Pass the storefront domain so backend can resolve the correct tenant
      const storefrontDomain = window.location.hostname;
      trackingUrl.searchParams.set('domain', storefrontDomain);

      if (productSlug) {
        trackingUrl.searchParams.set('product', productSlug);
      }

      // Extract UTM parameters if present
      const utmSource = searchParams.get('utm_source');
      const utmMedium = searchParams.get('utm_medium');
      const utmCampaign = searchParams.get('utm_campaign');

      if (utmSource) trackingUrl.searchParams.set('source', utmSource);
      if (utmMedium) trackingUrl.searchParams.set('medium', utmMedium);
      if (utmCampaign) trackingUrl.searchParams.set('campaign', utmCampaign);

      // Call tracking endpoint (pixel-style)
      await fetch(trackingUrl.toString(), {
        method: 'GET',
        credentials: 'include', // Important: include cookies
        mode: 'cors',
      });

      // Store affiliate code in localStorage for persistence across navigation
      tenantStorage.setItem('affiliateCode', refCode);

      console.log(`✅ Affiliate click tracked: ${refCode}`);
    } catch (error) {
      console.error('❌ Failed to track affiliate click:', error);
    }
  };

  return null; // This component doesn't render anything
};

